import { Navigate, useLocation } from 'react-router-dom';
import { useSessionStore } from '@stores/sessionStore';
import { useDomainStore } from '@stores/domainStore';
import { useEffect, useState } from 'react';

/**
 * Module identifiers that match the backend Modules constants.
 * Keep in sync with /src/GCXP.Api/Filters/RequiresModuleAttribute.cs
 */
export const Modules = {
  FnB: 'fnb',
  Housekeeping: 'housekeeping',
  Maintenance: 'maintenance',
  Spa: 'spa',
  Pool: 'pool',
  Guides: 'guides',
  Vouchers: 'vouchers',
  Messages: 'messages',
  Laundry: 'laundry',
  Folio: 'folio',
  VoiceCall: 'voice_call',
  // Before arrival: the stay's own modules (StagePortalModules on the server)
  Registration: 'registration',
  Documents: 'documents',
  // After checkout
  Review: 'review',
  LostProperty: 'lost_property',
} as const;

export type ModuleId = typeof Modules[keyof typeof Modules];

interface ModuleGuardProps {
  /** The module(s) required to access this route. Pass array for OR logic (any of). */
  requiredModule: ModuleId | ModuleId[];
  /** The wrapped component to render if access is granted */
  children: React.ReactNode;
  /** Optional fallback component to show when access is denied. Defaults to redirect to welcome. */
  fallback?: React.ReactNode;
}

/**
 * Route guard component that checks if the current guest session has access
 * to the required module(s).
 * 
 * IMPORTANT: This guard fetches FRESH permission data from the API on every mount.
 * This ensures that admin permission changes take effect immediately, even if
 * the user navigates via URL or has a cached session.
 * 
 * Usage:
 * ```tsx
 * <Route path="/housekeeping" element={
 *   <ModuleGuard requiredModule={Modules.Housekeeping}>
 *     <HousekeepingPage />
 *   </ModuleGuard>
 * } />
 * ```
 */
export function ModuleGuard({ requiredModule, children, fallback }: ModuleGuardProps) {
  const { session, isValid, isLoading, refreshSession } = useSessionStore();
  const { getBasePath } = useDomainStore();
  const location = useLocation();
  
  // Track if we've completed a fresh permission check for THIS navigation
  const [hasCheckedPermissions, setHasCheckedPermissions] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // On mount (or when location changes), always fetch fresh permissions
  useEffect(() => {
    const checkPermissions = async () => {
      setIsRefreshing(true);
      try {
        await refreshSession();
      } finally {
        setIsRefreshing(false);
        setHasCheckedPermissions(true);
      }
    };
    
    // Reset and re-check on every navigation to a guarded route
    setHasCheckedPermissions(false);
    checkPermissions();
  }, [location.pathname, refreshSession]);

  // Wait for session validation AND fresh permission check to complete — unless the session we already hold
  // allows this module: then the page shows at once (no blank flash on every page change) and the fresh
  // check runs behind it; if that check takes the module away, the redirect below still happens.
  const checking = isLoading || isRefreshing || !hasCheckedPermissions;
  if (checking && !(isValid && session && isModuleAllowed(session.availableModules, requiredModule))) {
    return null;
  }

  // If not authenticated after loading, redirect to welcome/auth
  if (!isValid || !session) {
    const basePath = getBasePath();
    return <Navigate to={`${basePath}/welcome`} replace />;
  }

  // Check module access using FRESH data from API
  const hasAccess = isModuleAllowed(session.availableModules, requiredModule);

  if (hasAccess) {
    return <>{children}</>;
  }

  // Access denied
  if (fallback) {
    return <>{fallback}</>;
  }

  // Default: redirect to welcome page with access denied message
  const basePath = getBasePath();
  return (
    <Navigate 
      to={`${basePath}/welcome`} 
      state={{ 
        accessDenied: true, 
        deniedModule: Array.isArray(requiredModule) ? requiredModule[0] : requiredModule,
        from: location.pathname 
      }} 
      replace 
    />
  );
}

/**
 * Checks if a module is allowed based on the availableModules list.
 * 
 * @param availableModules - The modules the session has access to. Null/undefined/empty = all allowed.
 * @param required - The module(s) to check. Array uses OR logic (any match = allowed).
 * @returns True if access is allowed
 */
export function isModuleAllowed(
  availableModules: string[] | null | undefined,
  required: ModuleId | ModuleId[]
): boolean {
  // Null or undefined means ALL modules are allowed (no restrictions)
  if (availableModules === null || availableModules === undefined) {
    return true;
  }
  
  // Empty array means NO modules are allowed (explicitly blocked)
  if (availableModules.length === 0) {
    return false;
  }

  const requiredArray = Array.isArray(required) ? required : [required];
  const normalizedAvailable = new Set(availableModules.map(m => m.toLowerCase()));

  // Check if ANY of the required modules is available (OR logic)
  return requiredArray.some(mod => normalizedAvailable.has(mod.toLowerCase()));
}

/**
 * Access denied component for inline display instead of redirect.
 */
export function ModuleAccessDenied({ moduleName }: { moduleName: string }) {
  const { getBasePath } = useDomainStore();
  
  return (
    <div className="d-flex flex-column align-items-center justify-content-center min-vh-100 p-4 text-center">
      <div className="mb-4" style={{ fontSize: '4rem' }}>🚫</div>
      <h2 className="mb-3">Service Not Available</h2>
      <p className="text-muted mb-4">
        The {moduleName} service is not available for your session type.
        Please contact the front desk if you need assistance.
      </p>
      <a 
        href={`${getBasePath()}/welcome`} 
        className="btn btn-primary"
      >
        Go Back Home
      </a>
    </div>
  );
}

export default ModuleGuard;
